import React, { FC, useState, useEffect } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import DOMPurify from 'dompurify';

interface IHTMLPreview {
  htmlContent: string;
  htmlStyle: string;
  label?: string;
}

/**
 * HTMLPreview component displays a live preview of HTML content with CSS styles.
 *
 * This component renders HTML content with applied styles in an isolated iframe
 * to prevent style conflicts with the parent application. It includes a toggle
 * to show/hide the preview panel and displays appropriate messages for empty
 * or invalid content.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.htmlContent - The HTML content to preview
 * @param {string} props.htmlStyle - The CSS styles to apply to the HTML content
 * @param {string} [props.label] - Optional label for the preview panel
 *
 * @returns {JSX.Element} A preview panel with HTML/CSS rendering
 *
 * @example
 * <HTMLPreview
 *   htmlContent="<div>Hello World</div>"
 *   htmlStyle=".container { color: red; }"
 *   label="Campaign Preview"
 * />
 */

const HTMLPreview: FC<IHTMLPreview> = ({ htmlContent, htmlStyle, label = 'Preview' }) => {
  const [isPreviewVisible, setIsPreviewVisible] = useState(true);
  const [previewContent, setPreviewContent] = useState('');

  useEffect(() => {
    // Debounce the preview update to avoid excessive re-renders
    const timeoutId = setTimeout(() => {
      if (!htmlContent && !htmlStyle) {
        setPreviewContent('');
        return;
      }

      // Sanitize HTML content to prevent XSS attacks
      const sanitizedContent = DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: [
          'div', 'span', 'p', 'a', 'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li', 'br', 'strong', 'em', 'b', 'i', 'u', 'small',
          'table', 'thead', 'tbody', 'tr', 'td', 'th', 'button', 'svg', 'path',
        ],
        ALLOWED_ATTR: [
          'class', 'id', 'style', 'href', 'src', 'alt', 'title', 'target',
          'width', 'height', 'viewBox', 'd', 'fill', 'stroke',
        ],
        ALLOW_DATA_ATTR: false,
        // Strict URI validation: only allow safe protocols
        // Explicitly blocks: javascript:, data:, vbscript:, file:, etc.
        ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel|callto|sms):\/?\/?/i,
      });

      // Sanitize CSS to prevent CSS-based XSS
      // Remove dangerous CSS properties and values
      const sanitizedStyle = htmlStyle
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/data:/gi, '')
        .replace(/expression\s*\(/gi, '')
        .replace(/<\/style/gi, '')
        .replace(/@import/gi, '')
        .replace(/behavior:/gi, '')
        .replace(/-moz-binding/gi, '');

      // Create a complete HTML document with sanitized content and styles
      const fullHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src https: data:;">
            <style>
              body {
                margin: 0;
                padding: 16px;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
                  'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
                  sans-serif;
              }
              ${sanitizedStyle}
            </style>
          </head>
          <body>
            ${sanitizedContent}
          </body>
        </html>
      `;
      setPreviewContent(fullHTML);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [htmlContent, htmlStyle]);

  const togglePreview = () => {
    setIsPreviewVisible(!isPreviewVisible);
  };

  const renderPreviewContent = () => {
    if (!htmlContent && !htmlStyle) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400 italic">
          No content to preview. Add HTML content and styles above.
        </div>
      );
    }

    if (!htmlContent) {
      return (
        <div className="flex items-center justify-center h-full text-gray-400 italic">
          Add HTML content to see the preview.
        </div>
      );
    }

    return (
      <iframe
        title="HTML Preview"
        srcDoc={previewContent}
        className="w-full h-full border-0"
        sandbox="allow-same-origin"
        style={{ minHeight: '300px' }}
      />
    );
  };

  return (
    <div className="flex flex-col w-full mb-4 border border-gray-300 rounded-md">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b border-gray-300 rounded-t-md">
        <h6 className="text-sm font-semibold text-gray-700">{label}</h6>
        <button
          type="button"
          onClick={togglePreview}
          className="flex items-center gap-1 text-sm text-dark-purple hover:text-purple-700"
          aria-label={isPreviewVisible ? 'Hide preview' : 'Show preview'}
        >
          {isPreviewVisible ? (
            <>
              <VisibilityOff fontSize="small" />
              <span>Hide</span>
            </>
          ) : (
            <>
              <Visibility fontSize="small" />
              <span>Show</span>
            </>
          )}
        </button>
      </div>
      {isPreviewVisible && (
        <div className="p-4 bg-white rounded-b-md" style={{ minHeight: '300px' }}>
          {renderPreviewContent()}
        </div>
      )}
    </div>
  );
};

export default HTMLPreview;
